import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from 'recharts';
import { Twitter, Linkedin, Link as LinkIcon, Sparkles, Copy } from 'lucide-react';
import axios from 'axios';
import { marketplaceAPI } from '../services/api';
import { useToast } from '../hooks/use-toast';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Hoisted-once chart style/config objects — avoids creating new {} on every
// re-render which Recharts treats as a prop change.
const CHART_MARGIN = { top: 24, right: 24, bottom: 24, left: 0 };
const TICK_DARK = { fontSize: 10, fill: '#94a3b8' };
const Y_DOMAIN = ['dataMin', 'dataMax'];
const TOOLTIP_STYLE = { background: '#0b0b14', border: '1px solid #334155', borderRadius: 6 };
const formatCurrency = (v) => `$${Number(v).toLocaleString()}`;

/**
 * Public, OG-rich strategy share page.
 * URL: /s/:strategyId
 * Backend has a server-rendered HTML mirror at /api/share/strategy/:id with full
 * OG meta tags and a PNG OG image at /api/og/strategy/:id.png — those take care
 * of Twitter/LinkedIn unfurls. This page is the user-facing rich version.
 */
const StrategyShare = () => {
  const { strategyId } = useParams();
  const { toast } = useToast();
  const [strategy, setStrategy] = useState(null);
  const [equity, setEquity] = useState([]);
  const [loading, setLoading] = useState(true);
  const [copying, setCopying] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        // No public single-strategy GET — fetch via list and find by id.
        // Cheap because list is small in MVP.
        const all = await marketplaceAPI.list('newest');
        const s = all.find((x) => x.id === strategyId);
        if (cancelled) return;
        setStrategy(s);
        if (s) {
          // Set rich page meta for in-app navigation crawls
          try {
            document.title = `${s.name} · Neon Alpha Strategy`;
            const setMeta = (n, v, prop = false) => {
              const sel = prop ? `meta[property="${n}"]` : `meta[name="${n}"]`;
              let m = document.head.querySelector(sel);
              if (!m) {
                m = document.createElement('meta');
                m.setAttribute(prop ? 'property' : 'name', n);
                document.head.appendChild(m);
              }
              m.setAttribute('content', v);
            };
            const ogImg = `${API}/og/strategy/${strategyId}.png`;
            const pageUrl = `${window.location.origin}/s/${strategyId}`;
            setMeta('description', (s.description || '').slice(0, 200));
            setMeta('og:title', `${s.name} · by ${s.author_name}`, true);
            setMeta('og:description', (s.description || '').slice(0, 200), true);
            setMeta('og:image', ogImg, true);
            setMeta('og:url', pageUrl, true);
            setMeta('twitter:card', 'summary_large_image');
            setMeta('twitter:image', ogImg);
          } catch (metaErr) {
            console.debug('og meta update failed:', metaErr);
          }

          // Try a quick backtest replay for the chart preview
          try {
            const r = await axios.post(`${API}/backtest/run`, {
              symbol: 'AAPL',
              strategy: s.strategy,
              params: s.params || {},
              period: '1y',
            }, { headers: { 'Content-Type': 'application/json' } });
            if (!cancelled) setEquity(r.data?.equity_curve || []);
          } catch (btErr) {
            console.debug('backtest replay failed; showing static OG image:', btErr);
          }
        }
      } catch (loadErr) {
        console.warn('Strategy load failed:', loadErr);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [strategyId]);

  if (loading) {
    return <div className="min-h-screen bg-[#0b0b14] text-gray-300 flex items-center justify-center">Loading strategy…</div>;
  }
  if (!strategy) {
    return (
      <div className="min-h-screen bg-[#0b0b14] text-gray-300 flex items-center justify-center flex-col gap-3">
        <p>Strategy not found.</p>
        <Link to="/marketplace"><Button variant="outline">Browse Marketplace</Button></Link>
      </div>
    );
  }

  const bs = strategy.backtest_summary || {};
  const url = `${window.location.origin}/s/${strategyId}`;
  const text = `${strategy.name} on Neon Alpha — ${bs.total_return_pct != null ? `${bs.total_return_pct >= 0 ? '+' : ''}${bs.total_return_pct}% return` : 'AI-built strategy'}`;
  const tw = `https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(url)}`;
  const li = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`;

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(url);
      toast({ title: 'Link copied' });
    } catch (e) {
      console.debug('clipboard write failed:', e);
      toast({ title: 'Copy failed', description: 'Please copy the URL manually.' });
    }
  };

  const copyToBacktester = async () => {
    setCopying(true);
    try {
      try {
        await marketplaceAPI.copy(strategyId);
      } catch (copyErr) {
        // Already-copied / not-signed-in are non-fatal; show a soft hint.
        console.debug('marketplace copy returned error:', copyErr);
      }
      toast({ title: 'Copied. Open Backtester to run.' });
    } finally { setCopying(false); }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#0b0b14] via-[#10101e] to-[#0b0b14] text-gray-100">
      <div className="max-w-3xl mx-auto px-4 py-12" data-testid="strategy-share-page">
        <Link to="/" className="text-cyan-300 hover:text-cyan-200 text-sm">← Neon Alpha</Link>

        <Badge className="mt-6 bg-purple-600/30 text-purple-200 border-purple-500/40">
          <Sparkles size={12} className="mr-1" /> Strategy
        </Badge>
        <h1 className="text-4xl font-bold mt-3 leading-tight" data-testid="strategy-share-name">{strategy.name}</h1>
        <p className="text-gray-400 mt-1">by <span className="text-gray-200 font-medium">{strategy.author_name}</span> · {strategy.strategy}</p>

        {/* Hero equity image OR live curve */}
        <div className="mt-8 rounded-xl overflow-hidden border border-purple-500/20 bg-[#0b0b14]">
          {equity.length > 0 ? (
            <div style={{ height: 320 }}>
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={equity} margin={CHART_MARGIN}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#1f2937" />
                  <XAxis dataKey="date" tick={TICK_DARK} minTickGap={50} />
                  <YAxis tick={TICK_DARK} domain={Y_DOMAIN} width={50} />
                  <Tooltip contentStyle={TOOLTIP_STYLE} formatter={formatCurrency} />
                  <Line type="monotone" dataKey="equity" stroke="#a855f7" strokeWidth={2} dot={false} name="Equity" />
                </LineChart>
              </ResponsiveContainer>
            </div>
          ) : (
            <img src={`${API}/og/strategy/${strategyId}.png`} alt={strategy.name} className="w-full" data-testid="strategy-og-image" />
          )}
        </div>

        {/* Metrics */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mt-6" data-testid="strategy-share-metrics">
          {[
            ['Return', bs.total_return_pct != null ? `${bs.total_return_pct >= 0 ? '+' : ''}${bs.total_return_pct}%` : '—', (bs.total_return_pct || 0) >= 0],
            ['CAGR', bs.cagr_pct != null ? `${bs.cagr_pct >= 0 ? '+' : ''}${bs.cagr_pct}%` : '—', (bs.cagr_pct || 0) >= 0],
            ['Sharpe', bs.sharpe ?? '—', (bs.sharpe || 0) > 0],
            ['Max DD', bs.max_dd_pct != null ? `${bs.max_dd_pct}%` : '—', false],
            ['Win Rate', bs.win_rate_pct != null ? `${bs.win_rate_pct}%` : '—', (bs.win_rate_pct || 0) >= 50],
          ].map(([k, v, good]) => (
            <Card key={k} className="bg-[#13132a] border-[#1f1f33]">
              <CardContent className="p-3">
                <p className="text-xs text-gray-500">{k}</p>
                <p className={`text-xl font-semibold ${good ? 'text-green-400' : 'text-red-400'}`}>{v}</p>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Description */}
        <p className="mt-6 text-gray-300 leading-relaxed">{strategy.description}</p>

        {/* Share buttons */}
        <div className="mt-8 flex flex-wrap gap-2" data-testid="strategy-share-buttons">
          <Button onClick={copyToBacktester} disabled={copying} className="bg-gradient-to-r from-purple-600 to-indigo-600 hover:opacity-90" data-testid="strategy-copy-btn">
            <Copy size={14} className="mr-1" /> {copying ? 'Copying…' : 'Copy to Backtester'}
          </Button>
          <Button variant="outline" className="border-gray-700 text-gray-200 hover:bg-gray-800" onClick={() => window.open(tw, '_blank', 'noopener,noreferrer')} data-testid="strategy-share-x">
            <Twitter size={14} className="mr-1" /> Share
          </Button>
          <Button variant="outline" className="border-gray-700 text-gray-200 hover:bg-gray-800" onClick={() => window.open(li, '_blank', 'noopener,noreferrer')} data-testid="strategy-share-linkedin">
            <Linkedin size={14} className="mr-1" /> Share
          </Button>
          <Button variant="outline" className="border-gray-700 text-gray-200 hover:bg-gray-800" onClick={copyLink} data-testid="strategy-share-copy">
            <LinkIcon size={14} className="mr-1" /> Copy Link
          </Button>
        </div>

        <div className="mt-12 text-center text-gray-500 text-sm">
          <Link to="/auth" className="text-purple-400 hover:text-purple-300">Sign up free</Link> to publish your own strategies.
        </div>
      </div>
    </div>
  );
};

export default StrategyShare;
